import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import {
  getRondoUserDetail,
  getRondoUserMarketing,
  setRondoUserFlags,
  type RondoBehavioral,
  type RondoPartnerLead,
  type RondoTipovacka,
  type RondoUdaje,
  type RondoUserDetailResponse,
  type RondoUserMarketing,
} from '../api/rondo';
import { useFeather } from '../shell/useFeather';
import { useTabParam, useTabParamMapped } from '../shell/useTabParam';
import { HelpdeskTicketsEmbed } from '../helpdesk/HelpdeskTicketsEmbed';
import { PageState } from '../components/arc/page-state/page-state';
import { Button } from '../components/arc/button/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/arc/tabs/tabs';
import { MetricCard } from '../components/arc/metric-card/metric-card';
import { ArcPanel, ArcTabs } from '../components/arc/primitives/arc-primitives';
import './user-detail.css';

const COUNTRY_FLAG: Record<string, string> = { CZ: '🇨🇿', SK: '🇸🇰', RO: '🇷🇴' };
const TIER_COLOR: Record<string, string> = { Bronze: '#cd7f32', Silver: '#94a3b8', Gold: '#e0a800', Platinum: '#6366f1' };
const COUNTRY_NAME: Record<string, string> = { CZ: 'Česko', SK: 'Slovensko', RO: 'Rumunsko' };
const TABS = ['Přehled', 'Údaje', 'Behavioral', 'Marketing', 'Partneři', 'Spiny', 'Logy', 'Výplaty', 'Objednávky', 'Tipovačka', 'Bell wins', 'Helpdesk'] as const;
type Tab = (typeof TABS)[number];
const TAB_SLUG: Record<Tab, string> = {
  'Přehled': 'prehled',
  'Údaje': 'udaje',
  'Behavioral': 'behavioral',
  'Marketing': 'marketing',
  'Partneři': 'partneri',
  'Spiny': 'spiny',
  'Logy': 'logy',
  'Výplaty': 'vyplaty',
  'Objednávky': 'objednavky',
  'Tipovačka': 'tipovacka',
  'Bell wins': 'bellwins',
  'Helpdesk': 'helpdesk',
};
const TAB_DESCRIPTION: Record<Tab, string> = {
  'Přehled': 'Souhrn účtu, aktivity a partnerských leadů',
  'Údaje': 'Identifikační, kontaktní a systémové údaje uživatele',
  'Behavioral': 'Chování, aktivita a doporučený čas komunikace',
  'Marketing': 'Historie komunikace a výkon marketingových kanálů',
  'Partneři': 'Partnerské odkazy přiřazené tomuto uživateli',
  'Spiny': 'Poslední herní spiny a související výhry',
  'Logy': 'Pohyby rondonů a peněžní transakce',
  'Výplaty': 'Historie žádostí o výplatu a jejich stav',
  'Objednávky': 'Objednávky produktů vytvořené uživatelem',
  'Tipovačka': 'Sázky, výhry, odměny a doklady',
  'Bell wins': 'Historie výher z Bell mechaniky',
  'Helpdesk': 'Tickety uživatele a jejich aktuální stav',
};

const money = (n: number) => n.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const intFmt = (n: number) => n.toLocaleString('cs-CZ');
function initials(name: string) {
  const w = name.split(/\s+/).filter(Boolean);
  return ((w[0]?.[0] ?? '') + (w[1]?.[0] ?? '')).toUpperCase();
}
function dateOnly(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }) : '-';
}
function relDateTime(iso: string | null) {
  if (!iso) return '-';
  const d = new Date(iso);
  const time = d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
  const now = new Date();
  const same = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (same(d, now)) return `Dnes ${time}`;
  if (same(d, y)) return `Včera ${time}`;
  return `${dateOnly(iso)} ${time}`;
}
function dateTime(iso: string | null) {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`;
}

const CREDIT_TYPE: Record<string, { label: string; sign: string; cls: string }> = {
  add: { label: 'Přičteno', sign: '+', cls: 't-add' },
  subtract: { label: 'Odečteno', sign: '−', cls: 't-subtract' },
  transfer: { label: 'Převod', sign: '', cls: 't-transfer' },
  bonus: { label: 'Bonus', sign: '+', cls: 't-bonus' },
};

const SPIN_TAGS: Record<string, { label: string; cls: string }> = {
  puzzle: { label: 'Puzzle', cls: 'tag-puzzle' },
  minigame: { label: 'Minihra', cls: 'tag-minigame' },
  tictactoe: { label: 'Piškvorky', cls: 'tag-game' },
  survey: { label: 'Anketa', cls: 'tag-survey' },
  rondons: { label: 'Rondony', cls: 'tag-rondons' },
  rfix: { label: 'Rondo fix', cls: 'tag-rondons' },
  nothing: { label: '- bez výhry', cls: 'tag-nothing' },
  wolt: { label: 'Wolt', cls: 'tag-promo' },
  welcome: { label: 'Uvítací', cls: 'tag-promo' },
  daily: { label: 'Denní', cls: 'tag-promo' },
  daily_bonus: { label: 'Denní bonus', cls: 'tag-promo' },
  weekend: { label: 'Víkend', cls: 'tag-promo' },
  cashback: { label: 'Cashback', cls: 'tag-promo' },
  bell: { label: 'Bell win', cls: 'tag-promo' },
  tournament: { label: 'Turnaj', cls: 'tag-promo' },
  tipovacka: { label: 'Tipovačka', cls: 'tag-promo' },
  freespin: { label: 'Free spin', cls: 'tag-promo' },
  bonus: { label: 'Bonus', cls: 'tag-promo' },
};
function SpinTags({ tags }: { tags: string[] }) {
  if (!tags?.length) return null;
  return (
    <>
      {tags.map((raw, i) => {
        const tag = String(raw).trim();
        if (!tag) return null;
        const m = tag.match(/^(\d+)r$/);
        if (m) return <span key={i} className="spin-tag tag-amount" title={tag}>{m[1]} rondonů</span>;
        const def = SPIN_TAGS[tag];
        if (def) return <span key={i} className={`spin-tag ${def.cls}`} title={tag}>{def.label}</span>;
        return <span key={i} className="spin-tag tag-default" title={tag}>{tag.replace(/_/g, ' ')}</span>;
      })}
    </>
  );
}

function EmptyTable({ msg }: { msg: string }) {
  return <div className="text-center py-5 text-muted">{msg}</div>;
}

const bigAvatar: CSSProperties = {
  width: 64, height: 64, borderRadius: 12,
  background: '#5b3f96', color: '#fff',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontWeight: 700, fontSize: '1.3rem', flexShrink: 0,
};
const pill: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
  borderRadius: 999, background: '#f3f4f6', fontSize: '0.78rem', fontWeight: 500,
};

export function UserDetailPage({ id, onBack, backLabel = 'Zpět na seznam' }: { id: number; onBack: () => void; backLabel?: string }) {
  const [data, setData] = useState<RondoUserDetailResponse | null>(null);
  const [tab, setTab] = useTabParamMapped<Tab>('tab', TAB_SLUG, 'Přehled');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    setLoading(true);
    setError(null);
    getRondoUserDetail(id)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRondoUserDetail(id)
      .then((d) => !cancelled && setData(d))
      .catch((e: unknown) => !cancelled && setError(e instanceof ApiError ? e.message : 'Načtení selhalo'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [id]);

  useFeather([data, tab, error]);

  return (
    <section className="rondo-user-detail p1-arc-scope">
      <Button className="rondo-back-button" variant="secondary" size="sm" onClick={onBack}>{backLabel}</Button>
      {loading ? (
        <PageState title="Načítám detail uživatele" loading />
      ) : error ? (
        <PageState title="Detail uživatele se nepodařilo načíst" message={error} />
      ) : !data ? null : (
        <Detail data={data} tab={tab} setTab={setTab} onRefresh={refresh} />
      )}
    </section>
  );
}

function Detail({ data, tab, setTab, onRefresh }: { data: RondoUserDetailResponse; tab: Tab; setTab: (t: Tab) => void; onRefresh: () => void }) {
  const u = data.user;
  const activityLabel = u.isActive90d ? 'Aktivní 90 dní' : 'Mimo 90 dní';
  const countryName = COUNTRY_NAME[u.country] ?? u.country;

  return (
    <>
      <div className="rondo-user-profile-hero">
          <div className="ru-hero-head">
            <div className="ru-hero-left">
              <div className="rondo-profile-avatar">{initials(u.name)}</div>
              <div className="flex-grow-1 min-w-0">
                <div className="rondo-profile-eyebrow">RONDO UŽIVATEL</div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <h2 className="rondo-profile-name">{u.name}</h2>
                  <Button type="button" variant="ghost" size="sm" className="ru-refresh-icon-btn" onClick={onRefresh} title="Aktualizovat data" aria-label="Aktualizovat data">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                  </Button>
                </div>
                <div className="rondo-profile-id">Rondo userId {u.id}{u.nickname ? ` · ${u.nickname}` : ''}</div>
                <div className="rondo-profile-activity">
                  <span className="text-muted">Registrace</span> <strong>{dateOnly(u.registeredAt)}</strong>
                  {' '}<span className="text-muted mx-1">→</span> <span className="text-muted">posl. login</span> <strong>{relDateTime(u.lastActiveAt)}</strong>
                  {' '}<span className="text-muted mx-1">·</span> <span className="text-muted">posl. sázka</span> <strong>{relDateTime(u.lastBetAt)}</strong>
                </div>
                <div className="rondo-profile-pills">
                  <span className="pill pill-flag"><span className="pill-flag-ico">{COUNTRY_FLAG[u.country]}</span>{countryName}</span>
                  <span className={`pill ru-status-${u.isActive90d ? 'active' : 'inactive'}`}>{activityLabel}</span>
                  {u.status === 'blocked' && <span className="pill ru-status-blocked">Blokován</span>}
                  {u.loyaltyTier && (
                    <span className="pill" style={{ background: TIER_COLOR[u.loyaltyTier] ?? '#64748b', color: '#fff', fontWeight: 700 }} title={u.loyaltyVipPoints != null ? `${u.loyaltyVipPoints.toLocaleString('cs-CZ')} VIP bodů` : 'Věrnostní tier'}>
                      ★ {u.loyaltyTier}
                    </span>
                  )}
                  {u.udaje.gender === 'Muž' || u.udaje.gender === 'M' ? <span className="pill">Muž</span> : u.udaje.gender === 'Žena' || u.udaje.gender === 'F' ? <span className="pill">Žena</span> : null}
                </div>
                <div className="rondo-profile-contact">
                  {u.email && <a className="pill" href={`mailto:${u.email}`} style={{ textDecoration: 'none' }}>✉️ {u.email}</a>}
                  {u.mobile && <span className="pill">📱 {u.mobile}</span>}
                </div>
              </div>
            </div>
            <div className="ru-hero-right">
              <MetricCard label="Zůstatek" value={u.cashBalance} decimals={2} suffix={`\u00a0${u.currency}`} context="Dostupné prostředky" />
              <MetricCard label="Rondony" value={u.credits} context="Dostupné na účtu" />
              {u.rondonyVault ? <MetricCard label="Rondony Vault" value={u.rondonyVault} context="Uložené rondony" /> : null}
            </div>
          </div>
        </div>

      <Tabs value={TAB_SLUG[tab]} onValueChange={(value) => { const next = TABS.find((item) => TAB_SLUG[item] === value); if (next) setTab(next); }} className="rondo-arc-detail-tabs">
        <div className="rondo-arc-detail-tabs-bar"><TabsList>
          {TABS.map((t) => <TabsTrigger key={t} value={TAB_SLUG[t]}>{t}</TabsTrigger>)}
        </TabsList></div>
        <TabsContent value={TAB_SLUG[tab]} className="rondo-arc-detail-panel">
        <div className={`rondo-detail-canvas rondo-detail-tab-${TAB_SLUG[tab]}`}>
          <header className="rondo-tab-heading">
            <div>
              <h2>{tab}</h2>
              <p>{TAB_DESCRIPTION[tab]}</p>
            </div>
          </header>
          {tab === 'Přehled' && <Prehled data={data} />}
          {tab === 'Údaje' && <Udaje data={data} />}
          {tab === 'Behavioral' && <BehavioralTab b={u.behavioral} shScore={u.shScore} />}
          {tab === 'Marketing' && <MarketingTab userId={u.id} />}
          {tab === 'Partneři' && <PartneriTab leads={u.partnerLeads} uid={String(u.id)} />}
          {tab === 'Spiny' && <SpinyTab data={data} />}
          {tab === 'Logy' && <LogyTab data={data} />}
          {tab === 'Výplaty' && <VyplatyTab data={data} />}
          {tab === 'Objednávky' && <ObjednavkyTab data={data} />}
          {tab === 'Tipovačka' && <TipovackaTab t={u.tipovacka} />}
          {tab === 'Bell wins' && <BellWinsTab data={data} />}
          {tab === 'Helpdesk' && (
            <HelpdeskTicketsEmbed email={u.email} />
          )}
        </div>
        </TabsContent>
      </Tabs>
    </>
  );
}

const SURVEY_LEADS: Record<string, { label: string; reward: number; fullLabel: string }> = {
  sazka: { label: 'Sazka', reward: 500, fullLabel: 'Sazka / Allwyn' },
  merkurxtip: { label: 'MerkurXtip', reward: 300, fullLabel: 'MerkurXtip' },
  lucky_bet: { label: 'Lucky Bet', reward: 200, fullLabel: 'Lucky Bet' },
  magic_planet: { label: 'Magic Planet', reward: 200, fullLabel: 'Magic Planet' },
  grandwin: { label: 'Grandwin', reward: 200, fullLabel: 'Grandwin' },
};
const SURVEY_ORDER = ['sazka', 'merkurxtip', 'lucky_bet', 'magic_planet', 'grandwin'];
const PARTNER_TO_CODE: Record<string, string> = {
  Sazka: 'sazka', 'Sazka / Allwyn': 'sazka', MerkurXtip: 'merkurxtip', 'Lucky Bet': 'lucky_bet',
  'Magic Planet': 'magic_planet', Grandwin: 'grandwin',
};

function Prehled({ data }: { data: RondoUserDetailResponse }) {
  const u = data.user;
  const bellTotal = u.bellWins.reduce((s, b) => s + b.amount, 0);
  const metrics = [
    { label: 'Zůstatek (reálný)', value: u.cashBalance, decimals: 2, suffix: `\u00a0${u.currency}`, context: 'Dostupné prostředky' },
    { label: 'Rondony', value: u.credits, context: 'Dostupné na účtu' },
    { label: 'Spiny 30 dní', value: u.spin30d, context: 'Herní aktivita' },
    { label: 'Credit ops 30 dní', value: u.creditLog.length, context: 'Pohyby rondonů' },
    { label: 'Wallet ops 30 dní', value: u.walletLog.length, context: 'Peněžní transakce' },
    { label: 'Výplaty celkem', value: data.payoffs.length, context: 'Žádosti o výplatu' },
    { label: 'Objednávky', value: u.orders.length, context: 'Celkem vytvořeno' },
    { label: 'Tipovačky 30 dní', value: u.tip30d, context: 'Účast v tipovačce' },
    { label: 'Bell win celkem', value: bellTotal, decimals: 2, suffix: `\u00a0${u.currency}`, context: 'Součet výher' },
  ];

  const byCode: Record<string, RondoPartnerLead | undefined> = {};
  for (const l of u.partnerLeads) {
    const code = PARTNER_TO_CODE[l.partner];
    if (code) byCode[code] = l;
  }
  const totalCompleted = u.partnerLeads.filter((l) => l.status === 'dokončeno').length;
  const totalOpen = u.partnerLeads.filter((l) => l.status === 'otevřeno').length;

  return (
    <div className="rondo-overview-layout">
      <section className="rondo-overview-metrics">
        <div className="rondo-section-intro"><div><span>AKTUÁLNÍ STAV</span><h3>Účet a aktivita</h3></div><small>Souhrn nejdůležitějších hodnot uživatele</small></div>
        <div className="kpi-grid rondo-overview-kpis">
        {metrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}
        </div>
      </section>

      <section className="rondo-overview-partners">
        <div className="rondo-section-intro"><div><span>PARTNERSKÉ LEADY</span><h3>Průzkumy uživatele</h3></div>
          <small>
            {totalCompleted > 0 ? <strong style={{ color: '#137333' }}>{totalCompleted}× vyplněno</strong> : 'nic vyplněno'}
            {totalOpen > 0 ? ` · ${totalOpen}× jen otevřeno` : ''}
          </small>
        </div>
        <div className="rondo-survey-grid">
          {SURVEY_ORDER.map((code) => {
            const def = SURVEY_LEADS[code] ?? { label: code, reward: 0, fullLabel: code };
            const it = byCode[code];
            let cls = 'lead-none';
            let status = 'nic';
            if (it && it.status === 'dokončeno') { cls = 'lead-completed'; status = '✓ vyplněno'; }
            else if (it && it.status === 'otevřeno') { cls = 'lead-opened'; status = '👁 otevřeno'; }
            return (
              <article key={code} className={`rondo-survey-card ${cls}`} title={`${def.fullLabel} (+${def.reward} rondonů)`}>
                <div className="rondo-survey-mark">{def.label.slice(0, 1)}</div>
                <div><strong>{def.label}</strong><small>Odměna +{def.reward} rondonů</small></div>
                <span>{status}</span>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function UdajeRow({ label, value }: { label: string; value: ReactNode }) {
  const empty = value == null || value === '' || value === '-';
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">{empty ? <span className="text-muted">-</span> : value}</div>
    </div>
  );
}
function ToggleRow({ label, id, checked, disabled, onChange }: { label: string; id: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="udaje-item">
      <div className="udaje-item-label">{label}</div>
      <div className="udaje-item-value">
        <div className="form-check form-switch m-0">
          <input className="form-check-input udaje-toggle" type="checkbox" role="switch" id={id} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        </div>
      </div>
    </div>
  );
}

function UdajeSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <ArcPanel className="udaje-section rondo-udaje-panel">
      <div className="udaje-section-head">{title}</div>
      <div className="udaje-card">{children}</div>
    </ArcPanel>
  );
}

function Udaje({ data }: { data: RondoUserDetailResponse }) {
  const u = data.user;
  const ud: RondoUdaje = u.udaje;
  const [mailTesting, setMailTesting] = useState(ud.mailTesting);
  const [superhrac, setSuperhrac] = useState(ud.superhrac);
  const [saving, setSaving] = useState<'testMail' | 'superHrac' | null>(null);
  const [flagErr, setFlagErr] = useState<string | null>(null);

  async function toggleFlag(field: 'testMail' | 'superHrac', value: boolean) {
    const setter = field === 'testMail' ? setMailTesting : setSuperhrac;
    const prev = field === 'testMail' ? mailTesting : superhrac;
    setter(value); setSaving(field); setFlagErr(null);
    try {
      await setRondoUserFlags(u.id, field === 'testMail' ? { testMail: value } : { superHrac: value });
    } catch (e) {
      setter(prev);
      setFlagErr(e instanceof ApiError && e.status === 403 ? 'Nemáš oprávnění (uzivatele:write).' : 'Uložení se nezdařilo.');
    } finally {
      setSaving(null);
    }
  }
  const genderLabel = ud.gender === 'M' ? 'Muž' : ud.gender === 'F' ? 'Žena' : ud.gender;
  return (
    <div className="udaje-cols rondo-udaje-cols">
      <div>
        <UdajeSection title="Identifikace">
          <UdajeRow label="Jméno" value={u.name} />
          <UdajeRow label="Vokativ" value={u.firstNameVocative} />
          <UdajeRow label="Nick" value={u.nickname} />
          <UdajeRow label="Pohlaví" value={genderLabel} />
          <UdajeRow label="Datum narození" value="-" />
          <UdajeRow label="Svátek" value="-" />
          <UdajeRow label="Email" value={u.email} />
          <UdajeRow label="Mailing status" value={ud.mailkitStatus} />
          <UdajeRow label="Mobil" value={u.mobile} />
          <UdajeRow label="Externí ID" value={<code>rondo userId {u.id}</code>} />
        </UdajeSection>

        <UdajeSection title="Adresa">
          <UdajeRow label="Země" value={COUNTRY_NAME[u.country] ?? u.country} />
          <UdajeRow label="Město" value="-" />
          <UdajeRow label="PSČ" value="-" />
        </UdajeSection>

        <UdajeSection title="Účet">
          <UdajeRow label="Měna" value={u.currency} />
          <UdajeRow label="Aktivita (90 dní)" value={u.isActive90d ? 'Aktivní' : 'Mimo období'} />
          {u.status === 'blocked' && <UdajeRow label="Stav účtu" value="Blokován" />}
          <UdajeRow label="Role" value="-" />
          <UdajeRow label="Registrace" value={dateOnly(u.registeredAt)} />
          <UdajeRow label="Posl. login" value={relDateTime(u.lastActiveAt)} />
          <UdajeRow label="Posl. sázka" value={relDateTime(u.lastBetAt)} />
          <UdajeRow label="První sázka" value="-" />
          <UdajeRow label="Newslettery" value="-" />
          <UdajeRow label="Souhlas s podmínkami" value="-" />
        </UdajeSection>

        <UdajeSection title="Akvizice">
          <UdajeRow label="Zdroj registrace" value={ud.acquisitionSource} />
          <UdajeRow label="Reg. bonus" value="-" />
          <UdajeRow label="UTM source" value="-" />
          <UdajeRow label="UTM medium" value="-" />
        </UdajeSection>

        <UdajeSection title="Mailing">
          <ToggleRow label="Zařadit do segmentu Mail testing" id="ru-toggle-test-mail" checked={mailTesting} disabled={saving !== null} onChange={(v) => void toggleFlag('testMail', v)} />
        </UdajeSection>

        <UdajeSection title="Segmenty">
          <ToggleRow label="Superhráč (segment Superhráči)" id="ru-toggle-super-hrac" checked={superhrac} disabled={saving !== null} onChange={(v) => void toggleFlag('superHrac', v)} />
          {flagErr && <div className="udaje-item"><div className="udaje-item-label" /><div className="udaje-item-value"><span className="text-danger">{flagErr}</span></div></div>}
        </UdajeSection>

        {u.status === 'blocked' && (
          <UdajeSection title="Sankce">
            <UdajeRow label="Ban date" value="-" />
            <UdajeRow label="Ban reason" value="-" />
            <UdajeRow label="Inactivated at" value="-" />
            <UdajeRow label="Inactivation reason" value="-" />
            <UdajeRow label="Reactivated at" value="-" />
          </UdajeSection>
        )}
      </div>
    </div>
  );
}

const MK_SUBTABS = ['Základní', 'Výpis'] as const;
type MkSub = (typeof MK_SUBTABS)[number];
const MK_CHANNEL_LABEL: Record<string, string> = { mail: 'E-mail', modal: 'Modal', notif: 'Notifikace', sms: 'SMS', wa: 'WhatsApp' };
const MK_STATE_LABEL: Record<string, string> = { sent: 'Odesláno', failed: 'Chyba', skipped: 'Přeskočeno', pending: 'Čeká', sending: 'Odesílá se', enriching: 'Příprava', enriched: 'Připraveno' };

function MkChannel({ title, rows }: { title: string; rows: Array<[string, string | number]> }) {
  return (
    <ArcPanel className="rondo-marketing-channel">
      <h3>{title}</h3>
      <dl>
        {rows.map(([k, v], i) => (
          <div key={i}><dt>{k}</dt><dd>{typeof v === 'number' ? intFmt(v) : v}</dd></div>
        ))}
      </dl>
    </ArcPanel>
  );
}

function MarketingTab({ userId }: { userId: number }) {
  const [data, setData] = useState<RondoUserMarketing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sub, setSub] = useState<MkSub>('Základní');
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRondoUserMarketing(userId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Načtení marketing dat selhalo'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);
  useFeather([data, sub]);

  if (loading) return <div className="text-muted py-4">Načítám marketing data…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!data) return null;
  const { email, modal, notif, recent } = data;
  const dt = (s: string | null) =>
    s ? new Date(s).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <>
      <ArcTabs items={MK_SUBTABS.map((t) => ({ value: t, label: t }))} value={sub} onChange={setSub} label="Marketing přehledy" className="rondo-marketing-subtabs" />

      {sub === 'Základní' ? (
        <>
          <div className="rondo-marketing-kpis">
            <MetricCard label="Přijaté e-maily" value={email.campaigns} context="Doručené kampaně" />
            <MetricCard label="Otevřené e-maily" value={email.opened} context={email.openRate != null ? `${email.openRate} % z doručených` : 'Unikátní otevření'} />
            <MetricCard label="Prokliky" value={email.clicked} context={email.clickRate != null ? `${email.clickRate} % z doručených` : 'Unikátní kliknutí'} />
            <MetricCard label="Notifikace" value={notif.delivered} context="Doručeno" />
            <MetricCard label="Modaly" value={modal.shown} context="Zobrazeno" />
          </div>

          <div className="rondo-marketing-channels">
            <MkChannel
              title="E-mail"
              rows={[
                ['Přijaté kampaně', email.campaigns],
                ['Otevřené (unik.)', email.opened],
                ['Otevření celkem', email.opensTotal],
                ['Prokliky (unik.)', email.clicked],
                ['Míra otevření', email.openRate != null ? `${email.openRate} %` : '-'],
                ['Míra prokliků', email.clickRate != null ? `${email.clickRate} %` : '-'],
                ['Naposledy odesláno', dt(email.lastSentAt)],
                ['Naposledy otevřeno', dt(email.lastOpenAt)],
              ]}
            />
            <MkChannel
              title="Notifikace"
              rows={[
                ['Doručeno', notif.delivered],
                ['Zobrazeno', notif.shown],
                ['Kliknuto', notif.clicked],
                ['Naposledy', dt(notif.lastAt)],
              ]}
            />
            <MkChannel
              title="Modaly"
              rows={[
                ['Zobrazeno', modal.shown],
                ['Kliknuto', modal.clicked],
                ['Zavřeno', modal.dismissed],
                ['Naposledy', dt(modal.lastAt)],
              ]}
            />
          </div>
        </>
      ) : recent.length === 0 ? (
        <div className="text-muted small">Žádné kampaně.</div>
      ) : (
        <div className="table-responsive-mobile">
          <table className="table aff-list-table sticky-thead mb-0">
            <thead>
              <tr>
                <th>Kanál</th>
                <th>Kampaň</th>
                <th>Odesláno</th>
                <th>Stav</th>
                <th className="text-center">Otevřeno</th>
                <th className="text-center">Proklik</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={`${r.id}-${i}`}>
                  <td><span className="badge bg-secondary-subtle text-secondary-emphasis">{MK_CHANNEL_LABEL[r.channel] ?? r.channel}</span></td>
                  <td>{r.name}</td>
                  <td className="text-muted">{dt(r.sentAt)}</td>
                  <td>{MK_STATE_LABEL[r.state] ?? r.state}</td>
                  <td className="text-center">
                    {r.openCount > 0 ? <span className="badge bg-success-subtle text-success">{r.openCount}×</span> : <span className="text-muted">-</span>}
                  </td>
                  <td className="text-center">
                    {r.clickCount > 0 ? <span className="badge bg-warning-subtle text-warning-emphasis">{r.clickCount}×</span> : <span className="text-muted">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const BH_SUBTABS = ['Základní', 'Superhráč', 'Počasí'] as const;
type BhSub = (typeof BH_SUBTABS)[number];
const BH_SUB_SLUG: Record<BhSub, string> = { 'Základní': 'zakladni', 'Superhráč': 'superhrac', 'Počasí': 'pocasi' };

function BhSubNav({ active, onSelect }: { active: BhSub; onSelect: (t: BhSub) => void }) {
  return <ArcTabs label="Behavioral přehledy" items={BH_SUBTABS.map((value) => ({ value, label: value }))} value={active} onChange={onSelect} className="rondo-bh-subtabs" />;
}

function BehavioralTab({ b, shScore }: { b: RondoBehavioral; shScore: number | null }) {
  const [sub, setSub] = useTabParamMapped<BhSub>('bsub', BH_SUB_SLUG, 'Základní', false);
  useFeather([sub]);
  return (
    <>
      <BhSubNav active={sub} onSelect={setSub} />
      {sub === 'Základní' && <BhZakladni b={b} />}
      {sub === 'Superhráč' && (
        <>
          <div className="udaje-section-title">Profil superhráče <span className="text-muted small" style={{ fontWeight: 400 }}>(skóre 0–100)</span></div>
          <BhSuperhrac b={b} shScore={shScore} />
        </>
      )}
      {sub === 'Počasí' && <BhPocasi b={b} />}
    </>
  );
}

function BhZakladni({ b }: { b: RondoBehavioral }) {
  const ph = b.byHour;
  const maxPh = Math.max(1, ...ph);
  const topEvent = b.topEvents[0]?.name ?? '-';
  const kpis: { label: string; value: string; hint: string }[] = [
    { label: 'Eventů celkem', value: intFmt(b.totalEvents), hint: `${b.topEvents.length} typů eventů` },
    { label: 'Top event', value: topEvent, hint: b.topEvents[0] ? `${intFmt(b.topEvents[0].count)}×` : '-' },
    { label: 'Nejlepší čas', value: `${b.bestSendTime}:00`, hint: 'nejvyšší aktivita' },
    { label: 'Partner affinity', value: intFmt(b.partnerAffinity.length), hint: 'sledovaných partnerů' },
  ];
  return (
    <>
      <div className="rondo-bh-kpis">
        {kpis.map((k) => (
          <ArcPanel className="rondo-bh-kpi" key={k.label}>
            <span>{k.label}</span>
            <strong>{k.value}</strong>
            <small>{k.hint}</small>
          </ArcPanel>
        ))}
      </div>

      <div className="row g-3">
        <div className="col-md-6">
          <ArcPanel className="ru-bh-card">
            <div className="ru-bh-card-head">Top eventy</div>
            {b.topEvents.length ? (
              <div className="table-responsive-mobile"><table className="ru-bh-mini-table">
                <thead><tr><th>Event</th><th className="text-end">Počet</th></tr></thead>
                <tbody>
                  {b.topEvents.map((t) => (
                    <tr key={t.name}>
                      <td><span className="ru-bh-evtype">{t.name}</span></td>
                      <td className="text-end fw-semibold">{intFmt(t.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            ) : <div className="text-muted small">Zatím žádné eventy.</div>}
          </ArcPanel>
        </div>
        <div className="col-md-6">
          <ArcPanel className="ru-bh-card">
            <div className="ru-bh-card-head">Aktivita per hodina</div>
            <div className="ru-bh-bars">
              {ph.map((v, h) => {
                const pct = Math.round((v / maxPh) * 100);
                return (
                  <div className={`ru-bh-bar-col${v < maxPh * .5 ? ' is-low' : ''}`} key={h} title={`${h}:00 - ${v} eventů`} aria-label={`${h}:00, ${v} eventů`}>
                    <div className="ru-bh-bar-val">{v > 0 ? v : ''}</div>
                    <div className="ru-bh-bar"><div className="ru-bh-bar-fill" style={{ height: `${pct}%`, ...(h === b.bestSendTime ? { background: '#16a34a' } : {}) }}></div></div>
                    <div className="ru-bh-bar-label">{h}</div>
                  </div>
                );
              })}
            </div>
          </ArcPanel>
        </div>
      </div>

      {b.partnerAffinity.length > 0 && (
        <ArcPanel className="ru-bh-card mt-3">
          <div className="ru-bh-card-head">Partner affinity (kliky z mailingu)</div>
          <div className="ru-bh-partners">
            {(() => {
              const maxPC = Math.max(1, ...b.partnerAffinity.map((p) => p.score));
              return b.partnerAffinity.map((p) => {
                const pct = Math.round((p.score / maxPC) * 100);
                const color = '#d9731f';
                return (
                  <div className="ru-bh-prt-row" key={p.partner}>
                    <span className="ru-bh-prt-dot" style={{ background: color }}></span>
                    <span className="ru-bh-prt-name">{p.partner}</span>
                    <div className="ru-bh-prt-bar"><div className="ru-bh-prt-bar-fill" style={{ width: `${pct}%`, background: color }}></div></div>
                    <span className="ru-bh-prt-count">{intFmt(p.score)}×</span>
                  </div>
                );
              });
            })()}
          </div>
        </ArcPanel>
      )}
    </>
  );
}

function BhSuperhrac({ b, shScore }: { b: RondoBehavioral; shScore: number | null }) {
  const sh = b.superhrac;
  const score = shScore ?? sh.score ?? 0;
  const cls = score >= 70 ? 'ru-sh-high' : score >= 40 ? 'ru-sh-mid' : 'ru-sh-low';
  const maxPts = 20;
  return (
    <ArcPanel className="ru-sh-profile-wrap">
      <div className="ru-sh-profile-head">
        <span className={`ru-sh-score ru-sh-score-big ${cls}`}>{score}</span>
        <span className="text-muted">/100 · {score >= 70 ? 'vysoké' : score >= 40 ? 'střední' : 'nízké'} skóre</span>
      </div>
      <div className="ru-sh-breakdown">
        {sh.dims.map((d) => {
          const pct = Math.round((d.points / maxPts) * 100);
          const barCls = pct >= 80 ? 'ru-sh-bar-full' : pct > 0 ? 'ru-sh-bar-part' : 'ru-sh-bar-empty';
          return (
            <div className="ru-sh-dim" key={d.name}>
              <div className="ru-sh-dim-head">
                <span className="ru-sh-dim-name">{d.name}</span>
                <span className="ru-sh-dim-total">{d.points} / {maxPts}</span>
                <div className="ru-sh-dim-bar"><div className={`ru-sh-dim-bar-fill ${barCls}`} style={{ width: `${pct}%` }}></div></div>
              </div>
            </div>
          );
        })}
      </div>
    </ArcPanel>
  );
}

function BhPocasi({ b }: { b: RondoBehavioral }) {
  if (!b.weather.length) return <ArcPanel className="rondo-bh-empty">Data o počasí nejsou pro tohoto uživatele dostupná.</ArcPanel>;
  const maxEng = Math.max(0.1, ...b.weather.map((w) => w.engagement));
  return (
    <ArcPanel className="ru-bh-card">
      <div className="ru-bh-card-head">Engagement per počasí</div>
      <div className="table-responsive-mobile"><table className="ru-bh-mini-table">
        <thead><tr><th>Bucket</th><th className="text-end">Events/den</th><th style={{ width: '35%' }}>Relativní</th></tr></thead>
        <tbody>
          {b.weather.map((w) => {
            const pct = Math.round((w.engagement / maxEng) * 100);
            return (
              <tr key={w.condition}>
                <td>{w.condition}</td>
                <td className="text-end fw-semibold">{w.engagement}</td>
                <td>
                  <div style={{ background: '#e5e7eb', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: '#d9731f' }}></div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table></div>
    </ArcPanel>
  );
}

function PartneriTab({ leads, uid }: { leads: RondoPartnerLead[]; uid: string }) {
  if (!leads.length) {
    return <div className="text-muted py-3 text-center">Žádné partner linky.</div>;
  }
  const groupColor = (name: string) => {
    const map: Record<string, string> = { Sazka: '#fbbf24', MerkurXtip: '#ef4444', 'Lucky Bet': '#3b82f6', 'Magic Planet': '#6366f1', Grandwin: '#22c55e' };
    return map[name] ?? '#64748b';
  };
  return (
    <div className="rondo-partners-layout">
      <ArcPanel as="aside" className="rondo-partner-identity">
        <span>IDENTIFIKÁTOR UŽIVATELE</span>
        <code>{uid || '-'}</code>
        <p>Hodnota se do partnerských adres vkládá jako <code>data1</code>. Mailová proměnná se při odeslání nahradí adresou konkrétního uživatele.</p>
      </ArcPanel>
      <section className="rondo-partner-cards">
      {leads.map((l) => {
        const ini = l.partner.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase();
        const color = groupColor(l.partner);
        return (
          <ArcPanel as="article" className="rondo-partner-card" key={l.partner}>
            <div className="rondo-partner-card-head">
              <div className="rondo-partner-logo" style={{ background: color }}>{ini}</div>
              <div><strong>{l.partner}</strong><small>Partnerský lead</small></div>
              <span className={`rondo-partner-state ${l.status === 'dokončeno' ? 'done' : ''}`}>{l.status || 'aktivní'}</span>
            </div>
            <div className="rondo-partner-variable"><span>Proměnná</span><code>{l.sharedVar}</code></div>
            <div className="rondo-partner-url">
              <span>Generovaná URL</span>
              <div>
                  {l.generatedUrl ? (
                    <>
                      <input type="text" readOnly value={l.generatedUrl} title={l.generatedUrl} />
                      <a href={l.generatedUrl} target="_blank" rel="noopener" title="Otevřít v novém okně"><i data-feather="external-link" style={{ width: 14, height: 14 }}></i></a>
                    </>
                  ) : <span className="text-muted small">-</span>}
              </div>
            </div>
          </ArcPanel>
        );
      })}
      </section>
    </div>
  );
}

function SpinyTab({ data }: { data: RondoUserDetailResponse }) {
  const spins = data.user.spins;
  if (!spins.length) return <EmptyTable msg="Žádné spiny." />;
  return (
    <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
      <thead><tr><th>Datum</th><th>SpinHistory ID</th><th>Game ID</th><th>Item ID</th><th>Win Dt</th><th>Order</th><th>Tags</th></tr></thead>
      <tbody>
        {spins.map((s) => (
          <tr key={s.spinHistoryId}>
            <td>{dateTime(s.date)}</td>
            <td>{s.spinHistoryId}</td>
            <td>{s.game}</td>
            <td>{s.item ?? <span className="text-muted">-</span>}</td>
            <td>{s.winDate ? dateTime(s.winDate) : <span className="text-muted">-</span>}</td>
            <td>{s.orderId ? `#${s.orderId}` : <span className="text-muted">-</span>}</td>
            <td><SpinTags tags={s.tags} /></td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

const LOG_SUBTABS = [
  { key: 'credit', label: 'Rondony' },
  { key: 'wallet', label: 'Peníze' },
] as const;
type LogSub = (typeof LOG_SUBTABS)[number]['key'];
const LOG_SUB_VALUES: readonly LogSub[] = ['credit', 'wallet'];

function LogyTab({ data }: { data: RondoUserDetailResponse }) {
  const [sub, setSub] = useTabParam<LogSub>('lsub', LOG_SUB_VALUES, 'credit', false);
  const u = data.user;
  return (
    <>
      <ArcTabs items={LOG_SUBTABS.map((s) => ({ value: s.key, label: s.label }))} value={sub} onChange={setSub} label="Typ logu" className="rondo-log-subtabs" />
      {sub === 'credit' ? (
        u.creditLog.length === 0 ? <EmptyTable msg="Žádný credit log." /> : (
          <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
            <thead><tr><th>Datum</th><th>Typ</th><th className="text-end">Částka</th><th>Měna</th><th>Poznámka</th></tr></thead>
            <tbody>
              {u.creditLog.map((c, i) => {
                const t = CREDIT_TYPE[c.type] ?? { label: c.type, sign: '', cls: 't-default' };
                const amtCls = c.type === 'add' || c.type === 'bonus' ? 'ru-amount-positive' : c.type === 'subtract' ? 'ru-amount-negative' : 'ru-amount-neutral';
                return (
                  <tr key={i} title={c.note}>
                    <td>{dateTime(c.date)}</td>
                    <td><span className={`credit-type ${t.cls}`}>{t.label}</span></td>
                    <td className={`text-end ${amtCls}`}>{t.sign}{intFmt(Math.abs(c.amount))}</td>
                    <td className="text-muted small">RONDON</td>
                    <td><span className="note-text">{c.note}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )
      ) : (
        u.walletLog.length === 0 ? <EmptyTable msg="Žádný wallet log." /> : (
          <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
            <thead><tr><th>Datum</th><th>Typ</th><th className="text-end">Částka</th><th>Měna</th><th>Poznámka / detail</th></tr></thead>
            <tbody>
              {u.walletLog.map((w, i) => {
                const amtCls = w.type === 'in' ? 'ru-amount-positive' : 'ru-amount-negative';
                return (
                  <tr key={i} title={w.note}>
                    <td>{dateTime(w.date)}</td>
                    <td><strong>{w.type}</strong></td>
                    <td className={`text-end ${amtCls}`}>{money(w.amount)} {w.currency}</td>
                    <td>{w.currency}</td>
                    <td className="text-muted small">{w.note}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )
      )}
    </>
  );
}

const PAYOFF_STATE_LABEL: Record<string, string> = {
  new: 'Nová', pending: 'Čeká', sent: 'Odesláno', delivered: 'Doručeno', rejected: 'Zamítnuto',
};
const PAYOFF_STATE_CLASS: Record<string, string> = {
  delivered: 'payout-paid', sent: 'payout-paid', new: 'payout-pending', pending: 'payout-pending', rejected: 'payout-rejected',
};
function VyplatyTab({ data }: { data: RondoUserDetailResponse }) {
  if (data.payoffs.length === 0) return <EmptyTable msg="Žádné výplaty." />;
  return (
    <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
      <thead><tr><th>Žádost</th><th>Doručeno</th><th className="text-end">Částka</th><th>Měna</th><th>Stav</th><th>Účet / IBAN</th><th>Provider</th></tr></thead>
      <tbody>
        {data.payoffs.map((p) => (
          <tr key={p.id}>
            <td>{dateTime(p.requestedAt)}</td>
            <td>{p.status === 'delivered' ? dateTime(p.requestedAt) : <span className="text-muted">-</span>}</td>
            <td className="text-end fw-semibold">{money(p.amount)}</td>
            <td>{p.currency}</td>
            <td><span className={`payout-status ${PAYOFF_STATE_CLASS[p.status] ?? 'payout-pending'}`}>{PAYOFF_STATE_LABEL[p.status] ?? p.status}</span></td>
            <td><span className="text-muted small">-</span></td>
            <td>-</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function ObjednavkyTab({ data }: { data: RondoUserDetailResponse }) {
  const orders = data.user.orders;
  if (orders.length === 0) return <EmptyTable msg="Žádné objednávky." />;
  return (
    <div className="table-responsive-mobile rondo-orders-table-wrap"><table className="table aff-list-table sticky-thead rondo-orders-table mb-0">
      <thead><tr><th>Order #</th><th>Vytvořeno</th><th>Stav</th><th>Product</th><th className="text-end">Cena</th><th>Detail</th></tr></thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.orderId}>
            <td>{o.orderId}</td>
            <td>{dateTime(o.createdAt)}</td>
            <td><span className="pill">{o.status}</span></td>
            <td>{o.product}</td>
            <td className="text-end">{money(o.price)} {o.currency}</td>
            <td className="text-muted small"></td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

const TIP_SUBTABS = [
  { key: 'bets', label: 'Sázky' },
  { key: 'winners', label: 'Výherci' },
  { key: 'rewards', label: 'Odměny' },
  { key: 'evidence', label: 'Důkazy' },
] as const;
type TipSub = (typeof TIP_SUBTABS)[number]['key'];
const TIP_SUB_VALUES: readonly TipSub[] = ['bets', 'winners', 'rewards', 'evidence'];

function TipovackaTab({ t }: { t: RondoTipovacka }) {
  const [sub, setSub] = useTabParam<TipSub>('tsub', TIP_SUB_VALUES, 'bets', false);
  useFeather([sub]);
  return (
    <>
      <div className="rondo-tip-kpis">
        <MetricCard label="Sázek celkem" value={t.summary.bets} context="Tipovačka" />
        <MetricCard label="Výher" value={t.summary.won} context="Úspěšné tipy" />
        <MetricCard label="Odměn" value={t.summary.rewards} context="Získané odměny" />
      </div>
      <ArcTabs items={TIP_SUBTABS.map((s) => ({ value: s.key, label: s.label }))} value={sub} onChange={setSub} label="Tipovačka přehledy" className="rondo-tip-subtabs" />
      <div className="tab-content">
        {sub === 'bets' && (
          t.bets.length === 0 ? <EmptyTable msg="Žádné sázky." /> : (
            <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
              <thead><tr><th>Datum</th><th>Hra</th><th>Tip</th><th className="text-center">Správně?</th><th className="text-end">Sázka</th></tr></thead>
              <tbody>
                {t.bets.map((b, i) => {
                  const correct = b.correct === true ? '✓' : b.correct === false ? '✗' : '';
                  const correctCls = b.correct === true ? 'text-success fw-bold' : b.correct === false ? 'text-danger' : 'text-muted';
                  return (
                    <tr key={i}>
                      <td>{dateTime(b.date)}</td>
                      <td>{b.game}</td>
                      <td className="text-muted">{b.tip}</td>
                      <td className={`text-center ${correctCls}`}>{correct}</td>
                      <td className="text-end">{intFmt(b.amount)} {b.currency}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )
        )}
        {sub === 'winners' && (
          t.winners.length === 0 ? <EmptyTable msg="Žádní výherci." /> : (
            <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
              <thead><tr><th>Datum</th><th>Hra</th><th>Detail</th></tr></thead>
              <tbody>
                {t.winners.map((w, i) => (
                  <tr key={i} title={w.detail}>
                    <td>{dateTime(w.date)}</td>
                    <td>{w.game}</td>
                    <td className="text-muted small">{w.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )
        )}
        {sub === 'rewards' && (
          t.rewards.length === 0 ? <EmptyTable msg="Žádné odměny." /> : (
            <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
              <thead><tr><th>Datum</th><th>Hra</th><th>Typ</th><th className="text-end">Částka</th><th>Detail</th></tr></thead>
              <tbody>
                {t.rewards.map((r, i) => (
                  <tr key={i}>
                    <td>{dateTime(r.date)}</td>
                    <td>{r.game}</td>
                    <td>{r.type}</td>
                    <td className="text-end ru-amount-positive">{intFmt(r.amount)} {r.currency}</td>
                    <td className="text-muted small"></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )
        )}
        {sub === 'evidence' && (
          t.proofs.length === 0 ? <EmptyTable msg="Žádné důkazy výher." /> : (
            <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
              <thead><tr><th>Datum</th><th>Hra</th><th>Detail</th></tr></thead>
              <tbody>
                {t.proofs.map((p, i) => (
                  <tr key={i} title={p.detail}>
                    <td>{dateTime(p.date)}</td>
                    <td>{p.game}</td>
                    <td className="text-muted small">{p.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )
        )}
      </div>
    </>
  );
}

function BellWinsTab({ data }: { data: RondoUserDetailResponse }) {
  const wins = data.user.bellWins;
  if (wins.length === 0) return <EmptyTable msg="Žádné bell wins." />;
  return (
    <div className="table-responsive-mobile"><table className="table aff-list-table sticky-thead mb-0">
      <thead><tr><th>Datum</th><th>Detail</th><th className="text-end">Částka</th></tr></thead>
      <tbody>
        {wins.map((w, i) => (
          <tr key={i}>
            <td>{dateTime(w.date)}</td>
            <td className="text-muted small">{w.detail}</td>
            <td className="text-end fw-semibold">{money(w.amount)} {w.currency}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}
